import { Box, CircularProgress, InputAdornment, Typography } from "@mui/material"
import { SearchOutlined } from "@mui-symbols-material/w300"
import React, { useCallback, useMemo, useState } from "react"
import { useFormContext } from "react-hook-form"

import { ManagerCheckbox } from "./ManagerCheckbox"

import { AerosTextField } from "@/components/UiKit/AerosTextField/AerosTextField"
import type { CompanyListFilter, User } from "@/graphql/codegen/graphql"

interface ManagerFilterProps {
  title: string
  managers: User[]
  fetching: boolean
  onFilterChange: (name: keyof CompanyListFilter, value: any) => void
}

// The checkbox list of managers. The caller decides which managers are listed and what the section is called.
export const ManagerFilter = React.memo<ManagerFilterProps>((props) => {
  const { title, managers: visibleManagers, fetching, onFilterChange } = props
  const [searchTerm, setSearchTerm] = useState("")
  const { control, watch } = useFormContext()
  const selectedManagerIds = watch("managers") || []

  const filteredManagers = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase()
    const seen = new Set<string>()

    return visibleManagers.filter((manager) => {
      const fullName = manager.name.toLocaleLowerCase()

      if (seen.has(manager.userId)) {
        return false
      }

      const isMatch = fullName.includes(normalizedSearchTerm)

      if (isMatch) {
        seen.add(manager.userId)
        return true
      }

      return false
    })
  }, [searchTerm, visibleManagers])

  const handleFilterChange = useCallback(
    (newValue: string[]) => {
      onFilterChange("managers", newValue) // Update the selected manager IDs in the filter
    },
    [onFilterChange]
  )

  return (
    <Box display='flex' flexDirection='column' height='100%'>
      <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
        {title}
      </Typography>

      <Box className='flex-none p-4'>
        <AerosTextField
          size='small'
          placeholder='Search by Name'
          value={searchTerm}
          onChange={setSearchTerm}
          data-testid='filters.manager-filter.search-field'
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined className='text-gray-600' />
                </InputAdornment>
              ),
              endAdornment: fetching ? <CircularProgress size={20} /> : undefined,
            },
          }}
        />
      </Box>

      <Box className='flex grow flex-col overflow-hidden'>
        <Box className='grow overflow-y-auto px-8 py-2'>
          {filteredManagers.map((manager) => (
            <ManagerCheckbox
              key={manager.userId}
              manager={manager}
              control={control}
              name='managers'
              selectedManagerIds={selectedManagerIds}
              onFilterChange={handleFilterChange}
            />
          ))}
          {fetching && (
            <Box display='flex' justifyContent='center' py={2}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
})
